import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box, Container, Stack, Typography, Button, TextField, IconButton, Divider, CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import { toast } from "react-toastify";

import api, { apiError, tokens as session } from "../api";
import openRazorpay from "../razorpayCheckout";
import { withGst } from "../gst";
import AppHeader from "../components/AppHeader";

/*
 * The page between choosing a plan and paying for it.
 *
 * Until now the upgrade dialog went straight to Razorpay's popup, which shows one figure
 * and nothing about what it is for. That is fine for a shop the customer already knows;
 * it is not fine for a subscription where the price on the plans page is before tax and
 * the amount actually taken is eighteen per cent higher. Somebody authorising ₹23,600
 * after reading ₹20,000 has every reason to think they have been overcharged.
 *
 * So: what is being bought, who it is billed to, what the tax is, and one total - the
 * same total printed on the button that charges it. Nothing is created at Razorpay until
 * that button is pressed, so leaving this page leaves nothing behind.
 */

const card = {
    p: { xs: 2.5, md: 3 },
    borderRadius: 3,
    bgcolor: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.1)",
};

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
};

const rupees = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

/** A labelled line in the billing panel. Dashes where a customer has not filled it in. */
const Field = ({ label, value }) => (
    <Box sx={{ mb: 1.6 }}>
        <Typography sx={{ color: "rgba(255,255,255,.45)", fontSize: 12 }}>{label}</Typography>
        <Typography sx={{ color: "#fff", fontSize: 14.5 }}>{value || "—"}</Typography>
    </Box>
);

export default function Checkout() {
    const navigate = useNavigate();
    const { state } = useLocation();

    const [account, setAccount] = useState(null);
    const [plan, setPlan] = useState(null);
    // The token's own row, for the paid days it still has left.
    const [row, setRow] = useState(null);
    const [gstin, setGstin] = useState("");
    const [busy, setBusy] = useState(false);

    // The code as typed, and the one the server has agreed to. Kept apart so a code that
    // is being edited does not keep showing a discount it no longer stands for.
    const [codeInput, setCodeInput] = useState("");
    const [coupon, setCoupon] = useState(null);
    const [checking, setChecking] = useState(false);

    /*
     * Everything needed is in the navigation state - which plant, which plan, which
     * period. Reached any other way (a bookmark, a reload) there is no order to show, so
     * it goes back rather than inventing one.
     */
    const order = state || {};

    useEffect(() => {
        if (!order.tokenId || !order.plan) { navigate("/account", { replace: true }); return; }

        api.get("/me")
            .then(({ data }) => { setAccount(data.account); setGstin(data.account.gstin || ""); })
            .catch(() => { session.account.clear(); navigate("/"); });

        api.get("/plans/sellable")
            .then(({ data }) => setPlan((data.data || []).find((p) => p.code === order.plan) || null))
            .catch(() => setPlan(null));

        api.get("/me/tokens")
            .then(({ data }) => setRow((data.data || []).find((t) => t.id === order.tokenId) || null))
            .catch(() => setRow(null));
    }, [navigate, order.tokenId, order.plan]);

    const yearly = order.period === "yearly";
    const listPrice = plan ? (yearly ? plan.priceYearly : plan.priceMonthly) : 0;
    /*
     * The discount comes off before tax - GST is due on what is actually paid. The same
     * arithmetic runs on the server when the subscription is created, and the server's
     * answer is the one that gets charged; this is only so the customer can see it.
     */
    const discount = coupon ? Math.round(listPrice * coupon.percent / 100) : 0;
    const bill = withGst(listPrice - discount);
    const per = yearly ? "year" : "month";

    /*
     * Days the customer has already paid for, and so the day the first charge falls.
     *
     * Nothing is taken today when there are days left - the mandate is authorised now
     * and the money moves when those days run out. The button has to say that, or
     * somebody presses "Pay" and then waits for a debit that is three weeks away.
     */
    const paidTill = row?.billing?.paidTill && new Date(row.billing.paidTill) > new Date()
        ? new Date(row.billing.paidTill)
        : null;
    const asDate = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

    /*
     * Only moving up replaces the days, and the page has to say which of the two this
     * is - it is the customer's money, and finding out afterwards is how a refund
     * request starts. Matches the rule in controllers/tokenBilling.js.
     */
    const movingUp = Boolean(plan && row) && plan.apiLimitPerDay > (row.apiLimitPerDay || 0);
    const firstCharge = paidTill && !movingUp ? asDate(paidTill) : null;
    const daysReplaced = paidTill && movingUp
        ? Math.ceil((paidTill.getTime() - Date.now()) / 86400000)
        : 0;

    const applyCoupon = async () => {
        setChecking(true);
        try {
            const { data } = await api.post("/coupons/check", { code: codeInput, product: "ai" });
            setCoupon({ code: data.code, percent: data.percent });
            toast.success(`${data.code} applied — ${data.percent}% off`);
        } catch (e) {
            setCoupon(null);
            toast.error(apiError(e));
        }
        setChecking(false);
    };

    const removeCoupon = () => { setCoupon(null); setCodeInput(""); };

    const pay = async () => {
        setBusy(true);
        try {
            /*
             * The GST number is saved before the subscription is created, because the
             * server reads it from the account when it tells Razorpay who to invoice.
             */
            const clean = gstin.trim().toUpperCase();
            if (clean !== (account.gstin || "")) await api.patch("/me", { gstin: clean });

            const { data } = await api.post(`/me/tokens/${order.tokenId}/subscribe`, {
                plan: order.plan,
                period: order.period,
                coupon: coupon?.code || "",
            });

            await openRazorpay(data, order.email, `${plan.name} plan`);
            navigate("/account");
        } catch (e) {
            toast.error(apiError(e));
            setBusy(false);
        }
    };

    return (
        <Box sx={{
            minHeight: "100vh", color: "#fff",
            display: "flex", flexDirection: "column",
            background:
                "radial-gradient(900px 520px at 12% 0%, rgba(46,125,32,.32), transparent 62%),"
                + "radial-gradient(760px 540px at 88% 100%, rgba(20,90,60,.28), transparent 64%), #07120B",
        }}>
            <AppHeader account={account} />

            <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 3 }}>
                    <IconButton onClick={() => navigate("/account")}
                        sx={{
                            color: "rgba(255,255,255,.75)",
                            border: "1px solid rgba(255,255,255,.14)",
                            bgcolor: "rgba(255,255,255,.04)",
                            "&:hover": { bgcolor: "rgba(255,255,255,.09)" },
                        }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 800, fontSize: 30, letterSpacing: -0.6 }}>Checkout</Typography>
                </Stack>

                {!plan ? (
                    <Stack sx={{ alignItems: "center", py: 8 }}><CircularProgress size={26} /></Stack>
                ) : (
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr" },
                        gap: 3, alignItems: "start",
                    }}>
                        <Stack spacing={3}>
                            {/* What is being bought, laid out as an order rather than a
                                sentence: the thing, what it allows, and its price. */}
                            <Box sx={card}>
                                <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>Your order</Typography>

                                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <Box sx={{ pr: 2 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 15.5 }}>
                                            MatrixIQ AI — {plan.name}
                                        </Typography>
                                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13.5, mt: .6 }}>
                                            {plan.apiLimitPerDay.toLocaleString("en-IN")} tokens a day
                                        </Typography>
                                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13.5 }}>
                                            Billed {yearly ? "yearly" : "monthly"}, renews on its own
                                        </Typography>
                                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13.5 }}>
                                            For plant {order.email}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 15.5, whiteSpace: "nowrap" }}>
                                        {rupees(listPrice)} / {per}
                                    </Typography>
                                </Stack>
                            </Box>

                            {/* Who the invoice is made out to. Read from the profile rather
                                than asked again here - except the GST number, which is the
                                one thing a customer usually remembers only at this point. */}
                            <Box sx={card}>
                                <Stack direction="row"
                                    sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Billed to</Typography>
                                    <Button size="small" onClick={() => navigate("/profile")}
                                        sx={{ color: "#9FE08C", fontSize: 13 }}>
                                        Edit details
                                    </Button>
                                </Stack>

                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                                    <Field label="Name" value={account?.name} />
                                    <Field label="Company" value={account?.companyName} />
                                    <Field label="Email" value={account?.email} />
                                    <Field label="Address"
                                        value={[account?.address, account?.city, account?.state, account?.pincode]
                                            .filter(Boolean).join(", ")} />
                                </Box>

                                <TextField size="small" fullWidth sx={{ ...darkField, mt: 1 }}
                                    label="GST number (optional)" value={gstin}
                                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                                    helperText="Printed on every invoice for this subscription. Saved for next time."
                                    slotProps={{ formHelperText: { sx: { color: "rgba(255,255,255,.4)" } } }} />
                            </Box>
                        </Stack>

                        {/* The money, on its own, with every line named. */}
                        <Box sx={{ ...card, position: { md: "sticky" }, top: { md: 24 } }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>Payment summary</Typography>

                            <Stack direction="row" sx={{ justifyContent: "space-between", py: .7 }}>
                                <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14 }}>Subtotal</Typography>
                                <Typography sx={{ fontSize: 14 }}>{rupees(listPrice)}</Typography>
                            </Stack>

                            {/* Only when there is one, and named - a line that just says
                                "discount" leaves the customer unsure what they used. */}
                            {coupon && (
                                <Stack direction="row" sx={{ justifyContent: "space-between", py: .7 }}>
                                    <Typography sx={{ color: "#9FE08C", fontSize: 14 }}>
                                        {coupon.code} ({coupon.percent}% off)
                                    </Typography>
                                    <Typography sx={{ color: "#9FE08C", fontSize: 14 }}>
                                        −{rupees(discount)}
                                    </Typography>
                                </Stack>
                            )}
                            <Stack direction="row" sx={{ justifyContent: "space-between", py: .7 }}>
                                <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14 }}>
                                    GST ({Math.round(bill.rate * 100)}%)
                                </Typography>
                                <Typography sx={{ fontSize: 14 }}>{rupees(bill.gst)}</Typography>
                            </Stack>

                            <Divider sx={{ my: 1.4, borderColor: "rgba(255,255,255,.12)" }} />

                            {/* Above the total, where a customer looks for it, and gone
                                once applied so there is nothing left to fiddle with. */}
                            {coupon ? (
                                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1.4 }}>
                                    <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13 }}>
                                        Coupon applied
                                    </Typography>
                                    <Button size="small" onClick={removeCoupon}
                                        sx={{ color: "rgba(255,255,255,.6)", fontSize: 12.5 }}>
                                        Remove
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={1} sx={{ mb: 1.8 }}>
                                    <TextField size="small" sx={{ ...darkField, flex: 1 }}
                                        label="Coupon code" value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                        onKeyDown={(e) => { if (e.key === "Enter" && codeInput.trim()) applyCoupon(); }} />
                                    {/*
                                      * The disabled colour is named, because MUI's own is
                                      * near-black - meant for a light page - and on this
                                      * paper the button simply disappears until something
                                      * is typed. A control nobody can see reads as a
                                      * missing feature, not as one waiting for input.
                                      */}
                                    <Button onClick={applyCoupon} disabled={!codeInput.trim() || checking}
                                        sx={{
                                            color: "#9FE08C", whiteSpace: "nowrap",
                                            "&.Mui-disabled": { color: "rgba(255,255,255,.35)" },
                                        }}>
                                        {checking ? "Checking…" : "Apply"}
                                    </Button>
                                </Stack>
                            )}

                            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Total payable</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#9FE08C" }}>
                                    {rupees(bill.total)}
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: "rgba(255,255,255,.45)", fontSize: 12.5, mt: .4 }}>
                                every {per}, until cancelled
                            </Typography>

                            {firstCharge && (
                                <Box sx={{
                                    mt: 1.6, px: 1.6, py: 1.2, borderRadius: 2,
                                    bgcolor: "rgba(46,125,32,.16)", border: "1px solid rgba(123,217,95,.3)",
                                }}>
                                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.85)" }}>
                                        Your {row?.plan} plan is paid until {firstCharge}, and you keep
                                        those days. Nothing is charged today - {plan?.name} begins
                                        on {firstCharge} and the first payment is taken then.
                                    </Typography>
                                </Box>
                            )}

                            {daysReplaced > 0 && (
                                <Box sx={{
                                    mt: 1.6, px: 1.6, py: 1.2, borderRadius: 2,
                                    bgcolor: "rgba(214,158,46,.14)", border: "1px solid rgba(243,201,105,.3)",
                                }}>
                                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.85)" }}>
                                        Your {row.plan} plan is paid until {asDate(paidTill)}.
                                        Changing plan starts a new period today, so those {daysReplaced} days
                                        are replaced and not refunded.
                                    </Typography>
                                </Box>
                            )}

                            <Button fullWidth variant="contained" disabled={busy || !bill.total} onClick={pay}
                                sx={{ mt: 2.5, borderRadius: 999, py: 1.2, fontWeight: 700 }}>
                                {busy
                                    ? "Opening payment…"
                                    : firstCharge
                                        ? `Confirm ${rupees(bill.total)} / ${per}`
                                        : `Pay ${rupees(bill.total)}`}
                            </Button>

                            <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: 12, mt: 1.6, lineHeight: 1.6 }}>
                                Payment is collected by Razorpay. {firstCharge
                                    ? "You are authorising the mandate now; the plan changes when the first payment is taken."
                                    : "Your plan changes once the first payment goes through,"} and you
                                can cancel any time from your account.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
